import { pillarArticles } from '@/mocks/pillarArticles';

export type Lang = 'pt' | 'de' | 'en';

export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://cssbuybr.sbs';

export const SITE_NAME = 'CSSBuy Spreadsheet Resource Hub';

export const HOME_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Stylish%20minimalist%20fashion%20collage%20featuring%20sneakers%2C%20a%20folded%20hoodie%2C%20sunglasses%20and%20a%20canvas%20tote%20bag%20arranged%20in%20an%20artistic%20grid%20on%20a%20soft%20warm%20cream%20background%2C%20premium%20editorial%20flat%20lay%20photography%20with%20soft%20natural%20shadows%2C%20clean%20airy%20composition%2C%20high%20detail%20textures%2C%20resource%20hub%20aesthetic&width=1600&height=900&seq=hero-main-01&orientation=landscape&nocache=true';

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType: string;
  ogImage?: string;
}

function normalizePath(pathname: string): string {
  if (pathname === '/') return '/';
  let p = pathname;
  if (!p.startsWith('/')) p = '/' + p;
  if (!p.endsWith('/')) p = p + '/';
  return p;
}

function detectLang(pathname: string): Lang {
  const seg = pathname.split('/').filter(Boolean)[0];
  if (seg === 'de') return 'de';
  if (seg === 'en') return 'en';
  return 'pt';
}

function normalizeLang(lang?: string): Lang {
  if (lang?.startsWith('de')) return 'de';
  if (lang?.startsWith('en')) return 'en';
  return 'pt';
}

interface RouteMeta {
  title: string;
  description: string;
  keywords: string;
}

const homeMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'CSSBuy Spreadsheet 2026 – Links, Guias, QC e Recursos',
    description:
      'Encontre o CSSBuy Spreadsheet com links, categorias, guias de compra, QC, shipping, warehouse e informações úteis para compradores. Recurso independente.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy links, CSSBuy guia, CSSBuy QC, CSSBuy shipping',
  },
  de: {
    title: 'CSSBuy Spreadsheet 2026 – Links, Anleitungen, QC und Ressourcen',
    description:
      'Finde den CSSBuy Spreadsheet mit Links, Kategorien, Kaufanleitungen, QC, Shipping, Warehouse und nützlichen Informationen für Käufer.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy Links, CSSBuy Anleitung, CSSBuy QC, CSSBuy Shipping',
  },
  en: {
    title: 'CSSBuy Spreadsheet 2026 – Links, Guides, QC and Resources',
    description:
      'Find the CSSBuy Spreadsheet with links, categories, buying guides, QC, shipping, warehouse and useful information for buyers. Independent resource.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy links, CSSBuy guide, CSSBuy QC, CSSBuy shipping',
  },
};

const spreadsheetMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'CSSBuy Spreadsheet – Links, Categorias e Recursos Organizados',
    description:
      'Explore o CSSBuy Spreadsheet com links organizados, categorias populares e recursos de compra para encontrar produtos com mais facilidade.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy lista, CSSBuy links, CSSBuy categorias',
  },
  de: {
    title: 'CSSBuy Spreadsheet – Links, Kategorien und Ressourcen',
    description:
      'Entdecke den CSSBuy Spreadsheet mit organisierten Links, Kategorien und Kaufressourcen für eine einfachere Produktsuche.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy Liste, CSSBuy Links, CSSBuy Kategorien',
  },
  en: {
    title: 'CSSBuy Spreadsheet – Links, Categories and Resources',
    description:
      'Explore the CSSBuy Spreadsheet with organized links, popular categories and buying resources to find products more easily.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy list, CSSBuy links, CSSBuy categories',
  },
};

const productsMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'Catálogo de Produtos CSSBuy – Tênis, Roupas e Acessórios',
    description:
      'Explore o catálogo completo de produtos do CSSBuy Spreadsheet: tênis, hoodies, camisetas, jaquetas, bolsas e acessórios com links diretos para compra.',
    keywords: 'CSSBuy produtos, CSSBuy tênis, CSSBuy roupas, CSSBuy acessórios',
  },
  de: {
    title: 'CSSBuy Produktkatalog – Schuhe, Kleidung und Accessoires',
    description:
      'Entdecke den vollständigen CSSBuy Produktkatalog: Schuhe, Hoodies, T-Shirts, Jacken, Taschen und Accessoires mit direkten Kauflinks.',
    keywords: 'CSSBuy Produkte, CSSBuy Schuhe, CSSBuy Kleidung, CSSBuy Accessoires',
  },
  en: {
    title: 'CSSBuy Product Catalog – Shoes, Clothing and Accessories',
    description:
      'Explore the complete CSSBuy Spreadsheet product catalog: shoes, hoodies, t-shirts, jackets, bags and accessories with direct purchase links.',
    keywords: 'CSSBuy products, CSSBuy shoes, CSSBuy clothing, CSSBuy accessories',
  },
};

const articleMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'CSSBuy Spreadsheet 2026: Guia Completo, Links, Categorias e Recursos',
    description:
      'Guia completo independente sobre CSSBuy Spreadsheet. Aprenda a usar, entenda QC, shipping, warehouse e encontre categorias populares.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy guia, CSSBuy QC, CSSBuy shipping, CSSBuy warehouse',
  },
  de: {
    title: 'CSSBuy Spreadsheet 2026: Vollständige Anleitung, Links und Kategorien',
    description:
      'Unabhängige Anleitung zum CSSBuy Spreadsheet. Erfahre, wie du es nutzt, QC, Shipping und Warehouse verstehst und Kategorien findest.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy Anleitung, CSSBuy QC, CSSBuy Shipping, CSSBuy Warehouse',
  },
  en: {
    title: 'CSSBuy Spreadsheet 2026: Complete Guide, Links and Resources',
    description:
      'Independent guide to the CSSBuy Spreadsheet. Learn how to use it, understand QC, shipping, warehouse and find popular categories.',
    keywords: 'CSSBuy Spreadsheet, CSSBuy guide, CSSBuy QC, CSSBuy shipping, CSSBuy warehouse',
  },
};

const aboutMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'Sobre Nós – CSSBuy Spreadsheet Resource Hub',
    description:
      'Saiba mais sobre o CSSBuy Spreadsheet Resource Hub, um recurso informacional independente com links, guias e informações de compra.',
    keywords: 'CSSBuy Spreadsheet, sobre nós, CSSBuy recurso, CSSBuy Brasil',
  },
  de: {
    title: 'Über uns – CSSBuy Spreadsheet Resource Hub',
    description:
      'Erfahre mehr über den CSSBuy Spreadsheet Resource Hub, eine unabhängige Informationsquelle mit Links, Anleitungen und Kaufinformationen.',
    keywords: 'CSSBuy Spreadsheet, über uns, CSSBuy Ressource, CSSBuy Deutschland',
  },
  en: {
    title: 'About Us – CSSBuy Spreadsheet Resource Hub',
    description:
      'Learn more about the CSSBuy Spreadsheet Resource Hub, an independent informational resource with links, guides and buying information.',
    keywords: 'CSSBuy Spreadsheet, about us, CSSBuy resource',
  },
};

const contactMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'Contato – CSSBuy Spreadsheet Resource Hub',
    description:
      'Entre em contato com o CSSBuy Spreadsheet Resource Hub para dúvidas, sugestões e informações sobre o CSSBuy Spreadsheet.',
    keywords: 'CSSBuy Spreadsheet, contato, CSSBuy suporte, CSSBuy dúvidas',
  },
  de: {
    title: 'Kontakt – CSSBuy Spreadsheet Resource Hub',
    description:
      'Kontaktiere den CSSBuy Spreadsheet Resource Hub für Fragen, Anregungen und Informationen zum CSSBuy Spreadsheet.',
    keywords: 'CSSBuy Spreadsheet, Kontakt, CSSBuy Support',
  },
  en: {
    title: 'Contact – CSSBuy Spreadsheet Resource Hub',
    description:
      'Contact the CSSBuy Spreadsheet Resource Hub for questions, suggestions and information about the CSSBuy Spreadsheet.',
    keywords: 'CSSBuy Spreadsheet, contact, CSSBuy support',
  },
};

const disclaimerMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'Aviso Legal – CSSBuy Spreadsheet Resource Hub',
    description:
      'Leia o aviso legal do CSSBuy Spreadsheet Resource Hub: um recurso independente, não afiliado ou endossado pelo CSSBuy.',
    keywords: 'CSSBuy Spreadsheet, aviso legal, CSSBuy disclaimer',
  },
  de: {
    title: 'Haftungsausschluss – CSSBuy Spreadsheet Resource Hub',
    description:
      'Lies den Haftungsausschluss des CSSBuy Spreadsheet Resource Hub: eine unabhängige Ressource, nicht mit CSSBuy verbunden.',
    keywords: 'CSSBuy Spreadsheet, Haftungsausschluss, CSSBuy Disclaimer',
  },
  en: {
    title: 'Disclaimer – CSSBuy Spreadsheet Resource Hub',
    description:
      'Read the disclaimer of the CSSBuy Spreadsheet Resource Hub: an independent resource, not affiliated with or endorsed by CSSBuy.',
    keywords: 'CSSBuy Spreadsheet, disclaimer, CSSBuy independent',
  },
};

const privacyMeta: Record<Lang, RouteMeta> = {
  pt: {
    title: 'Política de Privacidade – CSSBuy Spreadsheet Resource Hub',
    description:
      'Conheça a política de privacidade do CSSBuy Spreadsheet Resource Hub, incluindo coleta de dados, cookies e links externos.',
    keywords: 'CSSBuy Spreadsheet, privacidade, CSSBuy política, CSSBuy dados',
  },
  de: {
    title: 'Datenschutz – CSSBuy Spreadsheet Resource Hub',
    description:
      'Erfahre mehr über die Datenschutzrichtlinie des CSSBuy Spreadsheet Resource Hub, einschließlich Datenerfassung, Cookies und externe Links.',
    keywords: 'CSSBuy Spreadsheet, Datenschutz, CSSBuy Richtlinie',
  },
  en: {
    title: 'Privacy Policy – CSSBuy Spreadsheet Resource Hub',
    description:
      'Read the privacy policy of the CSSBuy Spreadsheet Resource Hub, including data collection, cookies and external links.',
    keywords: 'CSSBuy Spreadsheet, privacy, CSSBuy policy, CSSBuy data',
  },
};

export function getSeoForPath(pathname: string, lang?: Lang): SeoMeta {
  const resolvedLang: Lang = normalizeLang(lang ?? detectLang(pathname));
  const canonical = `${SITE_URL}${normalizePath(pathname)}`;

  const segments = pathname.split('/').filter(Boolean);
  let rest = segments;
  if (segments[0] === 'pt' || segments[0] === 'de' || segments[0] === 'en') {
    rest = segments.slice(1);
  }
  const route = rest.join('/');

  // Pillar articles (dynamic guides)
  if (route.startsWith('guides/')) {
    const slug = route.replace('guides/', '').replace(/\/$/, '');
    const article = pillarArticles.find((a) => a.slug === slug);
    if (article) {
      return {
        title: article.title,
        description: article.description,
        keywords: 'CSSBuy Spreadsheet, CSSBuy guia, CSSBuy QC, CSSBuy shipping',
        canonical,
        ogType: 'article',
        ogImage: article.image,
      };
    }
  }

  if (route === '' || route === 'cssbuy-spreadsheet') {
    const meta = route === 'cssbuy-spreadsheet' ? spreadsheetMeta[resolvedLang] : homeMeta[resolvedLang];
    return {
      ...meta,
      canonical,
      ogType: 'website',
      ogImage: route === 'cssbuy-spreadsheet' ? undefined : HOME_OG_IMAGE,
    };
  }

  if (route === 'products' || route === 'produtos' || route === 'produkte') {
    return { ...productsMeta[resolvedLang], canonical, ogType: 'website' };
  }

  if (
    route === 'cssbuy-spreadsheet-guia' ||
    route === 'cssbuy-spreadsheet-anleitung' ||
    route === 'cssbuy-spreadsheet-guide'
  ) {
    return { ...articleMeta[resolvedLang], canonical, ogType: 'article' };
  }

  if (route === 'about' || route === 'sobre' || route === 'ueber-uns') {
    return { ...aboutMeta[resolvedLang], canonical, ogType: 'website' };
  }

  if (route === 'contact' || route === 'contato' || route === 'kontakt') {
    return { ...contactMeta[resolvedLang], canonical, ogType: 'website' };
  }

  if (route === 'disclaimer' || route === 'aviso-legal' || route === 'haftungsausschluss') {
    return { ...disclaimerMeta[resolvedLang], canonical, ogType: 'website' };
  }

  if (route === 'privacy' || route === 'privacidade' || route === 'datenschutz') {
    return { ...privacyMeta[resolvedLang], canonical, ogType: 'website' };
  }

  // Fallback to home metadata
  return { ...homeMeta[resolvedLang], canonical, ogType: 'website', ogImage: HOME_OG_IMAGE };
}